import type { RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import { isEqual } from 'es-toolkit';
import type { FlyToOptions, Map } from 'maplibre-gl';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { DEFAULT_LANGUAGE, languageAliases, rtlLanguages } from './constants';
import type { MapPositionParams, MapStyle, PointType } from './map-types';
import { getStyleURL } from './utils/get-style-url';
import { setMapPosition } from './utils/set-map-position';

const INIT_MAP_ZOOM = 5;
const INIT_MAP_CENTER = { lng: 0, lat: 0 };

type MapInitOptions = {
  containerRef: RefObject<HTMLDivElement>;
  points: PointType[];
  center: 'auto' | { lng: number; lat: number };
  zoom: 'auto' | number;
  mapStyle: MapStyle;
  staticMode: boolean;
  locale?: string;
  widgetToken?: string;
  flyToOptions?: FlyToOptions;
};

export const useMapInit = ({
  containerRef,
  center,
  zoom,
  points,
  mapStyle,
  staticMode,
  locale,
  widgetToken,
  flyToOptions
}: MapInitOptions): Map | null => {
  const [map, setMap] = useState<Map | null>(null);

  const [isWidgetTokenReady, setIsWidgetTokenReady] = useState(false);
  const widgetTokenRef = useRef<string | undefined>(undefined);

  const isRTLPluginLoaded = useRef(false);

  useEffect(() => {
    if (locale && rtlLanguages.includes(locale) && !isRTLPluginLoaded.current) {
      maplibregl
        .setRTLTextPlugin(
          'https://unpkg.com/@mapbox/mapbox-gl-rtl-text@0.2.3/mapbox-gl-rtl-text.min.js',
          true
        )
        .then(() => {
          isRTLPluginLoaded.current = true;
        });
    }
  }, [locale]);

  useEffect(() => {
    if (widgetToken && !widgetTokenRef.current) {
      widgetTokenRef.current = widgetToken;
      setIsWidgetTokenReady(true);
    }
  }, [widgetToken]);

  useEffect(() => {
    if (!containerRef.current || !isWidgetTokenReady) return;

    const map = new maplibregl.Map({
      attributionControl: false,
      container: containerRef.current,
      style: getStyleURL(mapStyle, widgetTokenRef.current),
      center: INIT_MAP_CENTER, // starting position [lng, lat]
      zoom: INIT_MAP_ZOOM, // starting zoom,
      interactive: !staticMode,
      touchPitch: false,
      pitchWithRotate: false,
      dragRotate: false
    });

    map.on('load', () => {
      const language = (locale && languageAliases[locale]) ?? locale;

      if (!language) {
        return;
      }

      const symbolLayers = map
        .getStyle()
        .layers.filter(
          ({ type, id }) =>
            type === 'symbol' &&
            !/^(highway[_-]shield)|(road-number-shield)/.test(id)
        );

      symbolLayers.forEach(({ id }) => {
        map.setLayoutProperty(id, 'text-field', [
          'coalesce',
          ['get', `name:${language}`],
          ['get', `name:${DEFAULT_LANGUAGE}`],
          ['get', 'name']
        ]);
      });
    });

    setMap(map);

    if (!staticMode) {
      map.addControl(new maplibregl.NavigationControl({ showCompass: false }));
    }
    map.addControl(
      new maplibregl.AttributionControl({
        compact: false
      })
    );

    map.dragRotate.disable();
    map.touchZoomRotate.disableRotation();

    return () => {
      map.remove();
    };
  }, [isWidgetTokenReady, locale]);

  const prevPositionParamsRef = useRef<MapPositionParams | null>(null);

  useEffect(() => {
    if (!map) {
      return;
    }

    const handleLoad = () => {
      const currentParams = { zoom, center, points };
      const shouldRecalculateMapPosition =
        !prevPositionParamsRef.current ||
        !isEqual(prevPositionParamsRef.current, currentParams);

      if (shouldRecalculateMapPosition) {
        setMapPosition({
          map,
          center,
          zoom,
          points,
          flyToOptions
        });
      }

      prevPositionParamsRef.current = currentParams;
    };

    if (map.loaded()) {
      handleLoad();

      return;
    }

    map.on('load', handleLoad);

    return () => {
      map.off('load', handleLoad);
    };
  }, [map, points, center, zoom, flyToOptions]);

  return map;
};
